/**
 * Deux fenêtres sur la même contre-étude, et chacune au courant de ce que l'autre écrit.
 *
 * *Demande de Chris, 22/09/2026 : « je voudrais pouvoir ouvrir le planning dans une fenêtre
 * différente que je pourrais mettre sur un deuxième écran et en même temps consulter la
 * contre-étude, et à ce moment-là si je modifie quelque chose dans la contre-étude je peux
 * actualiser dans le planning sans le fermer, et vice versa. »*
 *
 * ## Pourquoi un canal, et pas un rappel de fenêtre à fenêtre
 *
 * `window.opener.postMessage` existe déjà ici — c'est le chemin du saut « remonter à la source »
 * (voir `cibles.ts`). Il ne convient pas pour ceci : il **désigne une fenêtre**, donc il faut la
 * tenir, savoir laquelle a ouvert laquelle, et il meurt dès que l'une des deux est rechargée.
 * `BroadcastChannel` ne désigne personne : il diffuse à toutes les pages de la **même origine**,
 * quel que soit l'ordre dans lequel elles ont été ouvertes, et une fenêtre rechargée se
 * réabonne toute seule. Trois fenêtres sur la même affaire fonctionnent sans une ligne de plus.
 *
 * Rien ne sort du navigateur : le canal ne traverse ni le réseau ni le serveur. Ce qui voyage
 * est un identifiant de version et rien d'autre — **aucune donnée de chantier**, aucun chiffre.
 * Ce que la fenêtre avertie affiche, elle va le relire au serveur, qui lui applique les mêmes
 * droits qu'à n'importe quelle lecture.
 *
 * ## Ce qui voyage est un signal, pas une valeur
 *
 * Le message dit « la version X a changé », jamais « voici ses nouveaux totaux ». C'est la même
 * règle que `appliquer` dans `App` : recalculer localement serait plus rapide et faux à la
 * première divergence. La fenêtre avertie **relit la version au serveur**, qui refait tourner
 * le moteur. Faire voyager des chiffres d'une fenêtre à l'autre, c'est se donner deux sources
 * pour la même grandeur, et le jour où elles divergent, aucune des deux écrans ne le dit.
 *
 * ## L'émetteur ne se relit pas lui-même
 *
 * `BroadcastChannel` ne renvoie pas à l'expéditeur — mais deux fenêtres ne sont pas la seule
 * configuration possible : le même document peut être ouvert deux fois dans la même fenêtre plus
 * tard, et un identifiant d'émetteur coûte une ligne. Il évite surtout la boucle : une fenêtre
 * qui se relirait sur son propre signal relancerait un chargement à chaque frappe.
 */

/** Le nom du canal. Partagé par toutes les pages de l'application, et par elles seules. */
const NOM_DU_CANAL = "planif:version-modifiee";

/** Le type du message, vérifié à la réception comme celui de `cibles.ts`. */
export const MESSAGE_VERSION_MODIFIEE = "planif:version-modifiee";

/** Ce qui traverse le canal. */
export type SignalDeVersion = {
  readonly type: typeof MESSAGE_VERSION_MODIFIEE;
  /** La version dont le contenu a bougé — contre-étude ou planning, c'est la même version. */
  readonly versionId: string;
  /** Qui l'a émis, pour que l'émetteur ne se relise pas lui-même. */
  readonly emetteur: string;
};

/**
 * L'identité de cette page, tirée une fois pour toutes à son chargement.
 *
 * `crypto.randomUUID` quand il est là, un tirage simple sinon : ce jeton ne protège rien, il
 * distingue deux pages ouvertes en même temps. Le confondre avec un secret serait une erreur de
 * lecture — il ne sort pas du navigateur et ne désigne ni une personne ni une session.
 */
export const MOI: string =
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `page-${String(Math.random()).slice(2)}`;

/**
 * Faut-il relire, sur ce message ?
 *
 * Écrit à part et **pur**, parce que c'est là que se trouvent les deux façons de se tromper :
 * relire sur le signal d'une autre version (deux affaires ouvertes côte à côte s'actualiseraient
 * l'une sur l'autre) et se relire sur son propre signal (une boucle). Un test s'en saisit sans
 * navigateur ni fenêtre.
 *
 * Le message arrive d'une autre page : il est **vérifié**, jamais supposé. Rien ne garantit sa
 * forme — une version future de l'application peut diffuser autre chose sur le même canal, et
 * c'est précisément le cas où il faut l'ignorer plutôt que de planter.
 */
export function doitRelire(message: unknown, versionId: string, moi: string): boolean {
  if (typeof message !== "object" || message === null) return false;
  const signal = message as Partial<SignalDeVersion>;
  if (signal.type !== MESSAGE_VERSION_MODIFIEE) return false;
  if (typeof signal.versionId !== "string" || typeof signal.emetteur !== "string") return false;
  // Une version vide ne désigne rien : l'écran sans version ouverte n'a rien à relire.
  if (versionId === "" || signal.versionId !== versionId) return false;
  return signal.emetteur !== moi;
}

/**
 * Le canal, ouvert à la demande et une seule fois.
 *
 * `null` quand le navigateur n'a pas `BroadcastChannel` : l'application continue, sans la
 * synchronisation. C'est aussi ce qui se passe sous le moteur de test, qui n'a pas de fenêtre —
 * et une fonction de synchronisation qui fait planter un test de calcul serait un mauvais
 * échange. Le bouton « Actualiser » de la fenêtre reste, lui, toujours là : c'est le chemin qui
 * ne dépend d'aucune capacité du navigateur.
 */
let canal: BroadcastChannel | null | undefined;

function leCanal(): BroadcastChannel | null {
  if (canal !== undefined) return canal;
  canal = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(NOM_DU_CANAL);
  return canal;
}

/**
 * Annoncer aux autres fenêtres que cette version vient de changer.
 *
 * À appeler **après** que le serveur a accepté la modification, jamais avant : annoncer une
 * écriture qui échoue ensuite ferait relire l'autre fenêtre pour rien, et pire, lui ferait
 * afficher un état que l'émetteur n'a pas.
 */
export function signalerVersionModifiee(versionId: string): void {
  if (versionId === "") return;
  const signal: SignalDeVersion = {
    type: MESSAGE_VERSION_MODIFIEE,
    versionId,
    emetteur: MOI,
  };
  leCanal()?.postMessage(signal);
}

/**
 * S'abonner aux modifications d'une version faites **ailleurs**.
 *
 * Rend la fonction de désabonnement, à appeler au démontage : un écouteur laissé derrière lui
 * relirait une version qu'on ne regarde plus, et ferait remonter sa réponse dans un composant
 * démonté.
 */
export function ecouterVersion(versionId: string, relire: () => void): () => void {
  const ouvert = leCanal();
  if (ouvert === null) return () => undefined;
  const surMessage = (evenement: MessageEvent<unknown>): void => {
    if (doitRelire(evenement.data, versionId, MOI)) relire();
  };
  ouvert.addEventListener("message", surMessage);
  return () => {
    ouvert.removeEventListener("message", surMessage);
  };
}
